import React, { useState, useEffect, useMemo } from "react";
import { mfiApi } from "../../../api";
import { normalizeCouponError } from "./couponErrors";
import { COLORS, formatDateTime, safeNumber, formatReference } from "./couponUi";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 639px)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function rowTimestamp(v) {
  const t = v ? Date.parse(v) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

function memberLabel(row) {
  if (row?.member && typeof row.member === "object" && row.member.name) return row.member.name;
  if (row?.member_reference != null && row.member_reference !== "") return String(row.member_reference);
  return "—";
}

function statusLabel(status) {
  if (status === "REVERSED") return "Reversed";
  if (status === "CONSUMED") return "Used";
  return status ? String(status) : "—";
}

const thStyle = {
  padding: "8px 10px",
  fontWeight: 600,
  textAlign: "left",
  whiteSpace: "nowrap",
};

export default function CouponUsageTable({ rows = [], familyId, couponType }) {
  const isMobile = useIsMobile();
  const [extraRows, setExtraRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [stopped, setStopped] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);

  const baseRows = Array.isArray(rows) ? rows : [];
  const allRows = useMemo(() => {
    const extras = Array.isArray(extraRows) ? extraRows : [];
    return [...baseRows, ...extras].sort((a, b) => rowTimestamp(b?.used_at) - rowTimestamp(a?.used_at));
  }, [baseRows, extraRows]);

  const canLoadMore = Boolean(familyId && couponType) && !stopped && (totalPages == null || page < totalPages);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const nextPage = page + 1;
      const res = await mfiApi.getCouponUsage(familyId, couponType, nextPage, 10);
      const results = Array.isArray(res?.results) ? res.results : [];
      setExtraRows((prev) => [...prev, ...results]);
      setPage(typeof res?.page === "number" ? res.page : nextPage);
      if (typeof res?.total_pages === "number") setTotalPages(res.total_pages);
      if (results.length === 0) setStopped(true);
    } catch (err) {
      const normalized = normalizeCouponError(err);
      setLoadMoreError(normalized || { code: "UNKNOWN", message: "Something went wrong. Please try again.", status: null });
    } finally {
      setLoadingMore(false);
    }
  };

  const cellBase = { padding: "8px 10px", fontSize: 12 };

  return (
    <div>
      {allRows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: COLORS.mediumText }}>No usage recorded</p>
      ) : isMobile ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {allRows.map((row, i) => {
            const reversed = row?.status === "REVERSED";
            return (
              <li
                key={row?.id != null ? row.id : i}
                style={{
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 8,
                  padding: 10,
                  backgroundColor: reversed ? COLORS.lightGrey : COLORS.white,
                  color: reversed ? COLORS.mediumText : COLORS.darkText,
                }}
              >
                <div style={{ fontSize: 11, textDecoration: reversed ? "line-through" : "none" }}>
                  <strong>Date:</strong> {formatDateTime(row?.used_at)}
                </div>
                <div style={{ fontSize: 11 }}>
                  <strong>Member:</strong> {memberLabel(row)}
                </div>
                <div style={{ fontSize: 11 }}>
                  <strong>Reference:</strong> {formatReference(row?.reference)}
                </div>
                <div style={{ fontSize: 11, textDecoration: reversed ? "line-through" : "none" }}>
                  <strong>Qty:</strong> {safeNumber(row?.quantity)}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: reversed ? COLORS.errorText : COLORS.darkText }}>
                  {statusLabel(row?.status)}
                </div>
                {reversed && row?.reversal_reason ? (
                  <div style={{ fontSize: 11, color: COLORS.errorText, marginTop: 2 }}>{row.reversal_reason}</div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.lightGrey, color: COLORS.mediumText }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Member</th>
                <th style={thStyle}>Reference</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map((row, i) => {
                const reversed = row?.status === "REVERSED";
                return (
                  <tr
                    key={row?.id != null ? row.id : i}
                    style={{
                      borderTop: `1px solid ${COLORS.mediumGrey}`,
                      backgroundColor: reversed ? COLORS.lightGrey : COLORS.white,
                      color: reversed ? COLORS.mediumText : COLORS.darkText,
                    }}
                  >
                    <td style={{ ...cellBase, textDecoration: reversed ? "line-through" : "none" }}>
                      {formatDateTime(row?.used_at)}
                    </td>
                    <td style={cellBase}>{memberLabel(row)}</td>
                    <td style={cellBase}>{formatReference(row?.reference)}</td>
                    <td style={{ ...cellBase, textAlign: "right", textDecoration: reversed ? "line-through" : "none" }}>
                      {safeNumber(row?.quantity)}
                    </td>
                    <td style={{ ...cellBase, fontWeight: 700, color: reversed ? COLORS.errorText : COLORS.darkText }}>
                      {statusLabel(row?.status)}
                      {reversed && row?.reversal_reason ? (
                        <div style={{ fontWeight: 400, fontSize: 11, color: COLORS.errorText }}>{row.reversal_reason}</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canLoadMore || loadingMore || loadMoreError ? (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
          {loadMoreError ? (
            <div
              role="alert"
              style={{
                width: "100%",
                backgroundColor: COLORS.errorBg,
                border: `1px solid ${COLORS.errorBorder}`,
                borderRadius: 8,
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span style={{ color: COLORS.errorText, fontSize: 12 }}>{loadMoreError.message}</span>
              <button
                type="button"
                onClick={handleLoadMore}
                style={{
                  backgroundColor: COLORS.white,
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  cursor: "pointer",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                Retry
              </button>
            </div>
          ) : null}
          {canLoadMore ? (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.mediumGrey}`,
                borderRadius: 6,
                padding: "6px 12px",
                cursor: loadingMore ? "not-allowed" : "pointer",
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.aquaBlue,
                opacity: loadingMore ? 0.6 : 1,
              }}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          ) : null}
          {loadingMore ? (
            <span role="status" style={{ fontSize: 11, color: COLORS.mediumText }}>
              Loading more usage…
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
